import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setGlobalSuccessMsg } from 'store/global/globalActions';
import { updateWebsiteLogin2FAConfig } from 'store/website/websiteActions';
import { WPSForm } from 'styles/layout/forms';
import { Content } from 'styles/globalStyles';
import { TitleBar } from 'styles/layout/titlebar';
import { Container } from 'styles/website/profile';
import { globalLogin2FAConfig } from 'store/setting/settingSelectors';
import { useSelector } from 'react-redux';
import { liveWebsitesSelector } from 'store/website/websiteSelectors';
import { updateGlobalLogin2FAConfig } from 'store/setting/settingActions';
import { useHistory } from 'react-router-dom';
import WebsiteHelper from 'helpers/website';
import StringHelper from 'helpers/string';
import UserHelper from 'helpers/user';
import JsxHelper from 'helpers/jsx';
import useTitle from 'hooks/useTitle';
import UserService from 'services/user';
import env from 'config/env';
import { isEmptyOrNull } from 'helpers';

const Login2FAConfig = ({ website }) => {
  useTitle(`${website ? 'Website - ' : ''}Login 2FA Settings`);

  const BOX_INNER_STYLE = { margin: '0 0 10px 0' };
  const isGlobalPage = !website;
  const history = useHistory();

  const dispatch = useDispatch();
  const [showResetButton, setShowResetButton] = useState(true);
  const allWebsites = useSelector(liveWebsitesSelector);
  
  // Handle global config based on user type
  const currentUserGlobalConfig = useSelector(globalLogin2FAConfig);
  const [partnerGlobalConfig, setPartnerGlobalConfig] = useState(null);
  const [configLoadingStatus, setConfigLoadinStatus] = useState(false);
  
  // Determine which global config to use
  const globalConfig = (() => {
    // If we're on global page or user is partner, use current user's config
    if (isGlobalPage || !UserHelper.isAdminOrAgent()) {
      return currentUserGlobalConfig;
    }
    // If we're admin/agent and have a website, use partner's config
    if (website && partnerGlobalConfig) {
      return partnerGlobalConfig;
    }
    // Default to current user config if partner config not loaded yet
    return currentUserGlobalConfig;
  })();
  
  const isGlobalConfigEnabled = globalConfig && globalConfig.enabled === 'yes';

  // ------------------------
  // INITIALIZATION
  // ------------------------

  // Fetch partner's global config if needed
  useEffect(() => {
    const fetchPartnerGlobalConfig = async () => {
      // Only fetch if we're admin/agent, have a website, and don't have config yet
      if (UserHelper.isAdminOrAgent() && website && !configLoadingStatus) {
        setConfigLoadinStatus('loading');
        try {
          const response = await UserService.getGlobalSettings({ user_slug: website.partner_slug });
          // If the response contains global login 2FA config, set it.
          const settings = response.websites?.global_login_2fa_config || {};
          if (!isEmptyOrNull(settings)) {
            setPartnerGlobalConfig(settings);
            window.logHelper.info('Partner global config fetched successfully', settings);
          }
        } catch (error) {
          window.logHelper.error('Failed to fetch partner global config:', error);
          // Fallback to current user config on error
        } finally {
          setConfigLoadinStatus('loaded');
        }
      }
    };

    fetchPartnerGlobalConfig();
  }, [website, partnerGlobalConfig, configLoadingStatus]);

  const INPUT_OPTIONS = useMemo(() => WebsiteHelper.getLogin2FAConfigOptions().map(item => {
    if (item.name === 'include_websites' || item.name === 'exclude_websites') {
      return {
        ...item,
        options: allWebsites.map(website => ({
          value: website.slug,
          label: WebsiteHelper.getLabel(website),
        })).concat(item.name === 'include_websites' ? [{ label: 'All Websites', value: 'all' },] : []),
        multiSelect: true,
      };
    }
    return item;
  }), [allWebsites]);
  const isWebsiteIncludedInGlobals = (() => {
    // The global config is not enabled or we are on the global page
    if (isGlobalPage || !isGlobalConfigEnabled) {
      return false;
    }
    // The website is excluded from global settings
    if (globalConfig.exclude_websites && globalConfig.exclude_websites.includes(website.slug)) {
      return false;
    }
    // If the global config includes websites, check if the current website is included
    if (globalConfig.include_websites && globalConfig.include_websites.some(slug => [ 'all', website.slug ].includes(slug))) {
      return true;
    }
    // Otherwise, it is not included in the global settings.
    return false;
  })();

  const isWebsiteExludedFromGlobalConfig = (() => {
    // If the global config is not enabled or we are on the global page, the website is not excluded
    if (isGlobalPage || !isGlobalConfigEnabled) {
      return false;
    }
    // The website is excluded from global settings
    if (globalConfig.exclude_websites && globalConfig.exclude_websites.includes(website.slug)) {
      return true;
    }
    // If the global config includes websites, check if the current website is included
    if (globalConfig.include_websites && globalConfig.include_websites.some(slug => [ 'all', website.slug ].includes(slug))) {
      return false;
    }
    // Otherwise, the website is not excluded from the global settings
    return false;
  })();

  const getCurrentConfig = useCallback((_website) => {
    // If we are on the global page, use the global config, otherwise use the website's config
    const config = isGlobalPage ? globalConfig : WebsiteHelper.getLogin2FAConfig(_website || website);
    
    // Do not include global options if we are on the website-specific page.
    // Global page? include all options, otherwise filter out global options.
    INPUT_OPTIONS.filter(item => isGlobalPage || !item.global).forEach(item => {
      const currentValue = config[item.name];
      if (!currentValue && currentValue !== '' && item.default !== undefined) {
        config[item.name] = item.default;
      }
      if (['include_websites', 'exclude_websites'].includes(item.name)) {
        config[item.name] = (globalConfig[item.name] || []).filter(slug =>
          allWebsites.find(website => website.slug === slug || slug === 'all')
        );
      }
    });
    
    // Format user_roles and user_caps to be newline separated if they are comma separated
    if (config.user_roles) {
      config.user_roles = config.user_roles.split(',').join('\n');
    }
    if (config.user_caps) {
      config.user_caps = config.user_caps.split(',').join('\n');
    }
    
    return config;
  }, [isGlobalPage, globalConfig, website, allWebsites, INPUT_OPTIONS]);

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(() => getCurrentConfig(website));
  const hasInitiallyLoaded = useRef(false);

  // Update details when global config changes (after partner config is loaded)
  // But only on initial load, not when user is actively making changes
  useEffect(() => {
    if (configLoadingStatus === 'loaded' && !isEmptyOrNull(globalConfig)) {
      const newConfig = getCurrentConfig(website);
      // Only update if this is the initial load
      if (!hasInitiallyLoaded.current) {
        setDetails(newConfig);
        hasInitiallyLoaded.current = true;
      } else {
        // For subsequent updates, only update if user hasn't made changes
        setDetails(prevDetails => {
          // If user has made changes (like override_global), don't reset
          if (prevDetails.override_global !== undefined && newConfig.override_global === undefined) {
            return prevDetails;
          }
          return newConfig;
        });
      }
    }
  }, [configLoadingStatus, globalConfig, website, getCurrentConfig]);

  // ------------------
  // HANDLERS
  // ------------------

  const goToGlobalSettings = () => history.push({ pathname: `/settings/login-2fa` });

  const onChange = (name, value) => {
    let newDetails = { ...details };
    
    if (name === 'include_websites') {
      const allSelected = value.includes('all');
      // If 'all' is selected, set the value to ['all'], otherwise keep the selected values.
      value = allSelected ? ['all'] : value;
      // Hide the exclude_websites option if 'all' is selected
      const excludeWebsitesIndex = INPUT_OPTIONS.findIndex(item => item.name === 'exclude_websites');
      if (excludeWebsitesIndex !== -1) {
        INPUT_OPTIONS[excludeWebsitesIndex].hide = allSelected;
      }
    } else if (name === 'override_global') {
      // When override_global changes, set enabled accordingly
      newDetails['enabled'] = value === 'yes' ? 'yes' : 'no';
    } else if (name === 'enabled' && !isGlobalPage && value === 'no') {
      // If the config is disabled, set override_global to 'no'
      newDetails['override_global'] = 'no';
    }
    
    // Set the main value
    newDetails[name] = value;
    
    setDetails(newDetails);
    setShowResetButton(true);
  }

  const resetDefaults = () => {
    const newDetails = {};
    INPUT_OPTIONS.forEach(item => {
      newDetails[item.name] = item.name !== 'enabled' ? item.default : details.enabled; // Keep the current enabled state
    });
    setDetails(newDetails);
    setShowResetButton(false);
  }

  // ------------------
  // API HANDLERS
  // ------------------

  const onSubmit = async () => {
    setLoading(true);
    const data = {
      ...details,
      enabled: details.enabled,
      user_roles: details.user_roles ? StringHelper.trim(details.user_roles.split('\n').join(','), ',') : '',
      user_caps: details.user_caps ? StringHelper.trim(details.user_caps.split('\n').join(','), ',') : '',
    };
    if (isGlobalPage) {
      submitGlobalSettings(data);
    } else {
      submitWebsiteSettings(data);
    }
  };

  const submitWebsiteSettings = (data) => {
    data.website_slug = website.slug;
    dispatch(updateWebsiteLogin2FAConfig(data))
      .then((website) => {
        setDetails(getCurrentConfig(website));
        dispatch(setGlobalSuccessMsg({ model: 'Login 2FA config', forId: WebsiteHelper.getLabel(website), action: 'updated' }))
      })
      .finally(() => setLoading(false));
    }

  const submitGlobalSettings = (data) => {
    data.include_websites = data.include_websites || [];
    data.exclude_websites = data.exclude_websites || [];
    if (!data.include_websites.includes('all')) {
      data.exclude_websites = []; // If 'all' is not selected, clear the exclude_websites
    }
    dispatch(updateGlobalLogin2FAConfig({ config: data }))
      .then(() => dispatch(setGlobalSuccessMsg({ model: 'Global Login 2FA config', action: 'updated' })))
      .finally(() => setLoading(false));
  };

  // ------------------
  // RENDER
  // ------------------

  const filterFormOptions = (item) => {
    // If the website is covered by global settings, special handling is needed.
    if (isWebsiteIncludedInGlobals) {
      // Do not display the 'enabled' option; it is already implied by the global settings.
      if (item.name === 'enabled') {
        return false;
      }
      // Always display the options that override global settings.
      if (item.name === 'override_global') {
        return true;
      }
      // If the 'override_global' is set to 'no', do not display any other options.
      if (details.override_global === 'no' && item.name !== 'override_global') {
        return false;
      }
    }
    // If the website is not inclcuded in the global settings, do not display the 'override_global' option.
    if (!isWebsiteIncludedInGlobals && item.name === 'override_global') {
      return false;
    }
    // If the config is not enabled, only display the 'enabled' option.
    if (item.name !== 'enabled' && details.enabled !== 'yes') {
      return false;
    }
    // If the item is hidden, do not display it
    if (item.hide) {
      return false;
    }
    // If 'all' is selected in include_websites, exclude_websites should not be displayed
    if (item.name === 'exclude_websites' && details.include_websites && !details.include_websites.includes('all')) {
      return false;
    }
    // If the item is global, it should only be displayed on the global page
    if (item.global && !isGlobalPage) {
      return false;
    }
    // If the item is website only, it should not be displayed on the global page.
    if (item.websiteOnly && isGlobalPage) {
      return false;
    }
    return true;
  }

  const renderForGlobalSettingsOrWebsite = () => {
    return <Fragment>
      {isWebsiteIncludedInGlobals && renderNotifceForCoveredWebsiteByGlobalSettings()}
      <div style={{ maxWidth: '500px', }}> {
        INPUT_OPTIONS.filter(filterFormOptions).map((item, index) =>
          <WPSForm.RowItem key={index} margin='0px'>
            {(item.options || item.type === 'select') && JsxHelper.createSelectInput({
              style: { width: '90%' },
              class: 'secondary-box-select',
              label: item.title,
              tooltip: item.desc,
              sortOff: true,
              options: item.options,
              value: details[item.name],
              multiSelect: item.multiSelect,
              isSearachable: item.multiSelect,
              onChange: (e) => {
                const value = item.multiSelect ? (e.target.values || []).map(item => item.value) : e.target.value;
                return onChange(item.name, value)
              }
            })}
            {item.type === 'textarea' && JsxHelper.createTextareaInput({
              style: { width: '90%' },
              class: 'secondary-box-select',
              label: item.title,
              placeholder: item.placeholder,
              tooltip: item.desc,
              value: details[item.name],
              onChange: (e) => onChange(item.name, e.target.value)
            }, index)}
          </WPSForm.RowItem>
        )}
      </div>
      <div className='action-buttons display-flex-nowrap margin-bottom-30' style={{display: 'flex'}}>
        {JsxHelper.createButton({
          label: 'Save',
          loading,
          onClick: onSubmit,
        })}
        {showResetButton ? details['enabled'] === 'yes' && JsxHelper.createButton({
          label: 'Reset',
          onClick: resetDefaults,
          classes: 'warning--btn',
        }) : null}
      </div>
    </Fragment>
  }

  // If the global settings is enabled and the website is included in the global settings, render the form
  // that allows users to configure 2FA for the website and override the global settings.
  const renderNotifceForCoveredWebsiteByGlobalSettings = () => {
    return <Fragment>
      <div className='box-wrapper' style={ {maxWidth: '50%'} }>
        {JsxHelper.createTipBox(
          <div>
            <h3 style={BOX_INNER_STYLE}>
              <span role="img" aria-label="Info">ℹ️</span> Website Covered by Global Settings
            </h3>
            <p style={BOX_INNER_STYLE}>
              This website is currently covered by the global Two-Factor Authentication (2FA) settings. 
              You can customize the settings below to override the global configuration for this specific website, 
              or <strong>leave the settings as-is to continue using the global configuration</strong>.
            </p>
          </div>, 
          true
        )}
      </div>
    </Fragment>
  }

  // If the site is excluded from global settings, display a notice.
  const renderNoticeForExcludedWebsite = () => {
    return <Fragment>
      <div className='box-wrapper' style={ {maxWidth: '50%'} }>
        {JsxHelper.createWarningBox(
          <div>
            <h3 style={BOX_INNER_STYLE}>
              <span role="img" aria-label="Warning">⚠️</span> Website Excluded from Global Settings
            </h3>
            <p style={BOX_INNER_STYLE}>
              This website is currently excluded from the global Two-Factor Authentication (2FA) settings.
              You cannot modify 2FA settings for this website until it is removed from the exclusion list.
            </p>
            {UserHelper.isPartner() ? (
              <p style={BOX_INNER_STYLE}>
                To modify Two-Factor Authentication for this website, go to <strong><span className='box-link' onClick={goToGlobalSettings}>Global Settings</span></strong> and remove it from the excluded websites list.
              </p>
            ) : (
              <p style={BOX_INNER_STYLE}>
                Please contact your administrator to remove this website from the excluded websites list in <strong>Global Settings</strong>.
              </p>
            )}
          </div>, 
          true
        )}
      </div>
    </Fragment>
  }

  const render = () => {
    // Show loading state while fetching partner global config
    if (configLoadingStatus === 'loading' && UserHelper.isAdminOrAgent() && website) {
      return <div>Loading global configuration...</div>;
    }
    
    // If the website is excluded from global settings, render a notice.
    if (isWebsiteExludedFromGlobalConfig) {
      return renderNoticeForExcludedWebsite();
    }
    // Otherwise, render the form for global settings or website-specific settings.
    return renderForGlobalSettingsOrWebsite();
  }

  return (
    <Container className='margin-0'>
      <TitleBar className='titlebar'>
        <TitleBar.Title>WordPress Login 2FA</TitleBar.Title>
      </TitleBar>
      <p className='color-primary subheader'>
        {env.getBrandShortName()} provides an option to enable Two-Factor Authentication (2FA) for your WordPress login for enhanced security. See our {JsxHelper.createFaqLink('wordpress-2fa', 'WordPress 2FA guide')} to learn how it works.
      </p>
      <Content>{render()}</Content>
    </Container>
  );
};

export default Login2FAConfig;
